import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import MicRecorder from "mic-recorder-to-mp3";
import { application } from "globals/endpoints";
import { useMaterialUIController } from "context";
import CloseIcon from "@mui/icons-material/Close";
import {
  Stack,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Box,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { setRTCEvent } from "context";
import MDBox from "components/MDBox";

const AudioRecorder = ({
  uploadUrl,
  method = "POST",
  headers = {},
  fieldName = "file",
  filename,
  bitRate = 128,
  uploadAs = "form",
  autoUpload = false,
  extraFormData = {},
  onUploadStart,
  onUploadProgress,
  onUploadSuccess,
  onUploadError,
  setIsAnnouncement,
  setAnnouncementDepartment,
}) => {
  const recorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);
  const [controller, dispatch] = useMaterialUIController();
  const { RTCEvent, darkMode } = controller;

  const computedFilename = useMemo(() => {
    if (filename) return filename;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    return `recording-${ts}.mp3`;
  }, [filename]);

  // auto start recording if RTCEvent is announcement
  useEffect(() => {
    if (RTCEvent && RTCEvent.type === "announcement" && !isRecording) {
      startRecording();
    }
    // eslint-disable-next-line
  }, [RTCEvent]);

  useEffect(() => {
    recorderRef.current = new MicRecorder({ bitRate });
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [bitRate]);

  const startTimer = () => {
    setSeconds(0);
    timerRef.current = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const requestPermissionIfNeeded = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionBlocked(false);
      return true;
    } catch (err) {
      console.error("Microphone permission denied or unavailable", err);
      setPermissionBlocked(true);
      setError("Microphone permission denied or unavailable.");
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError("");
    const ok = await requestPermissionIfNeeded();
    if (!ok) return;
    try {
      await recorderRef.current.start();
      setIsRecording(true);
      startTimer();
    } catch (err) {
      console.error("Failed to start recording", err);
      setError("Failed to start recording.");
    }
  }, [requestPermissionIfNeeded]);

  const stopRecording = useCallback(async () => {
    stopTimer();
    try {
      const [, mp3Blob] = await recorderRef.current.stop().getMp3();
      const url = URL.createObjectURL(mp3Blob);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlob(mp3Blob);
      setBlobUrl(url);
      setIsRecording(false);
      if (autoUpload) {
        setTimeout(() => {
          uploadRecording(mp3Blob);
        }, 0);
      }
    } catch (err) {
      console.error("Failed to stop or encode recording", err);
      setError("Failed to stop or encode recording.");
      setIsRecording(false);
    }
    // eslint-disable-next-line
  }, [blobUrl, autoUpload]);

  const reset = () => {
    stopTimer();
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl("");
    setError("");
    setProgress(0);
  };

  const uploadRecording = useCallback(
    async (inputBlob) => {
      const dataBlob = inputBlob || blob;
      if (!uploadUrl || !dataBlob) return;
      setError("");
      setUploading(true);
      setProgress(0);
      try {
        onUploadStart && onUploadStart({ filename: computedFilename });

        if (uploadAs === "form") {
          const formData = new FormData();
          formData.append(fieldName, dataBlob, computedFilename);
          if (extraFormData && typeof extraFormData === "object") {
            Object.entries(extraFormData).forEach(([k, v]) => {
              if (v !== undefined && v !== null) {
                formData.append(k, String(v));
              }
            });
          }
          const res = await application({
            url: uploadUrl,
            method: method.toUpperCase(),
            data: formData,
            onUploadProgress: (evt) => {
              if (evt.total) {
                const pct = Math.round((evt.loaded * 100) / evt.total);
                setProgress(pct);
                onUploadProgress && onUploadProgress(pct, evt);
              }
            },
          });
          onUploadSuccess && onUploadSuccess(res);
        } else {
          const res = await application({
            url: uploadUrl,
            method: method.toUpperCase(),
            data: dataBlob,
            headers: {
              "Content-Type": "audio/mpeg",
              ...headers,
            },
            onUploadProgress: (evt) => {
              if (evt.total) {
                const pct = Math.round((evt.loaded * 100) / evt.total);
                setProgress(pct);
                onUploadProgress && onUploadProgress(pct, evt);
              }
            },
          });
          onUploadSuccess && onUploadSuccess(res);
        }
        setIsAnnouncement(false);
        setAnnouncementDepartment("");
        setRTCEvent(dispatch, null);
        reset();
      } catch (err) {
        console.error("Upload failed", err);
        setError("Upload failed. See console for details.");
        onUploadError && onUploadError(err);
      } finally {
        setUploading(false);
      }
    },
    [
      uploadUrl,
      blob,
      fieldName,
      computedFilename,
      headers,
      method,
      uploadAs,
      extraFormData,
      onUploadStart,
      onUploadProgress,
      onUploadSuccess,
      onUploadError,
      dispatch,
    ]
  );

  const fmtTime = (total) => {
    const m = Math.floor(total / 60)
      .toString()
      .padStart(2, "0");
    const s = (total % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleClose = () => {
    stopRecording();
    setIsAnnouncement(false);
    setAnnouncementDepartment("");
    reset();
    setRTCEvent(dispatch, null);
  };

  return (
    <Card
      sx={{
        mt: 2,
        background: darkMode
          ? "linear-gradient(135deg, rgba(20, 20, 40, 0.95) 0%, rgba(30, 30, 60, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(240, 248, 255, 0.9) 0%, rgba(220, 240, 255, 0.9) 100%)",
        border: `2px solid ${
          darkMode
            ? "rgba(66, 165, 245, 0.4)"
            : "rgba(33, 150, 243, 0.3)"
        }`,
        borderRadius: "16px",
        boxShadow: darkMode
          ? "0 12px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(66, 165, 245, 0.1)"
          : "0 12px 32px rgba(33, 150, 243, 0.2), inset 0 1px 0 rgba(66, 165, 245, 0.15)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: darkMode
            ? "radial-gradient(circle at 20% 50%, rgba(66, 165, 245, 0.05) 0%, transparent 50%)"
            : "radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.03) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Stack spacing={2} sx={{ p: 3, position: "relative", zIndex: 1 }}>
        {/* Header */}
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: `1.5px solid ${
              darkMode
                ? "rgba(66, 165, 245, 0.2)"
                : "rgba(33, 150, 243, 0.15)"
            }`,
          }}
        >
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isRecording && (
              <FiberManualRecordIcon
                sx={{
                  color: "#ff4444",
                  fontSize: 20,
                  animation: "pulse 1s infinite",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      opacity: 1,
                    },
                    "50%": {
                      opacity: 0.5,
                    },
                  },
                }}
              />
            )}
            <MDTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: darkMode
                  ? "linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)"
                  : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              🎙️ Recording Announcement
            </MDTypography>
          </MDBox>
          <IconButton
            color="error"
            onClick={handleClose}
            sx={{
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "rotate(90deg) scale(1.1)",
                background: darkMode
                  ? "rgba(244, 67, 54, 0.1)"
                  : "rgba(244, 67, 54, 0.08)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </MDBox>

        {/* Recording Timer */}
        {isRecording && (
          <MDBox
            sx={{
              textAlign: "center",
              py: 2,
              background: darkMode
                ? "rgba(244, 67, 54, 0.1)"
                : "rgba(244, 67, 54, 0.05)",
              borderRadius: "12px",
              border: `1.5px solid ${
                darkMode
                  ? "rgba(244, 67, 54, 0.3)"
                  : "rgba(244, 67, 54, 0.2)"
              }`,
            }}
          >
            <MDTypography
              sx={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#ff4444",
                letterSpacing: "3px",
                fontFamily: "'Courier New', monospace",
              }}
            >
              {fmtTime(seconds)}
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{
                color: darkMode ? "#90caf9" : "#1976d2",
                mt: 0.5,
                fontWeight: 600,
              }}
            >
              Recording in progress...
            </MDTypography>
          </MDBox>
        )}

        {/* Permission Error */}
        {permissionBlocked && (
          <MDBox
            sx={{
              py: 2,
              px: 2,
              background: "rgba(244, 67, 54, 0.1)",
              borderRadius: "12px",
              border: "1.5px solid rgba(244, 67, 54, 0.3)",
            }}
          >
            <MDTypography
              variant="body2"
              sx={{
                color: "#ff4444",
                fontWeight: 600,
              }}
            >
              🔒 Microphone permission blocked. Please allow access.
            </MDTypography>
          </MDBox>
        )}

        {/* Action Buttons */}
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 0.5,
            width: "100%",
          }}
        >
          {!isRecording && (
            <MDButton
              variant="contained"
              onClick={startRecording}
              disabled={uploading}
              sx={{
                background: "linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)",
                color: "#ffffff",
                textShadow: darkMode ? "0 2px 4px rgba(0, 0, 0, 0.6)" : "0 1px 2px rgba(0, 0, 0, 0.3)",
                fontWeight: 700,
                fontSize: "13px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                px: 2,
                py: 1.3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.7,
                borderRadius: "10px",
                boxShadow: darkMode
                  ? "0 6px 20px rgba(76, 175, 80, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 6px 20px rgba(76, 175, 80, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover:not(:disabled)": {
                  transform: "translateY(-4px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 12px 32px rgba(76, 175, 80, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 12px 32px rgba(76, 175, 80, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                },
                "&:disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            >
              <MicIcon sx={{ fontSize: 18 }} />
              Start
            </MDButton>
          )}
          {isRecording && (
            <MDButton
              variant="contained"
              onClick={stopRecording}
              sx={{
                background: "linear-gradient(135deg, #f44336 0%, #ef5350 50%, #e53935 100%)",
                color: "#ffffff",
                textShadow: darkMode ? "0 2px 4px rgba(0, 0, 0, 0.6)" : "0 1px 2px rgba(0, 0, 0, 0.3)",
                fontWeight: 700,
                fontSize: "13px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                px: 2,
                py: 1.3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.7,
                borderRadius: "10px",
                boxShadow: darkMode
                  ? "0 6px 20px rgba(244, 67, 54, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  : "0 6px 20px rgba(244, 67, 54, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                  pointerEvents: "none",
                },
                "&:hover": {
                  transform: "translateY(-4px) scale(1.05)",
                  boxShadow: darkMode
                    ? "0 12px 32px rgba(244, 67, 54, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                    : "0 12px 32px rgba(244, 67, 54, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                },
              }}
            >
              <StopCircleIcon sx={{ fontSize: 18 }} />
              Stop
            </MDButton>
          )}
          <MDButton
            variant="contained"
            onClick={reset}
            disabled={isRecording || uploading || !blob}
            sx={{
              background: "linear-gradient(135deg, #9c27b0 0%, #7b1fa2 50%, #6a1b9a 100%)",
              color: "#ffffff",
              textShadow: darkMode ? "0 2px 4px rgba(0, 0, 0, 0.6)" : "0 1px 2px rgba(0, 0, 0, 0.3)",
              fontWeight: 700,
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              px: 2,
              py: 1.3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.7,
              borderRadius: "10px",
              boxShadow: darkMode
                ? "0 6px 20px rgba(156, 39, 176, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                : "0 6px 20px rgba(156, 39, 176, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
              transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                pointerEvents: "none",
              },
              "&:hover:not(:disabled)": {
                transform: "translateY(-4px) scale(1.05)",
                boxShadow: darkMode
                  ? "0 12px 32px rgba(156, 39, 176, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                  : "0 12px 32px rgba(156, 39, 176, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
            Reset
          </MDButton>
          <MDButton
            variant="contained"
            onClick={() => uploadRecording()}
            disabled={!blob || uploading || !uploadUrl || isRecording}
            sx={{
              background: "linear-gradient(135deg, #2196f3 0%, #1e88e5 50%, #1976d2 100%)",
              color: "#ffffff",
              textShadow: darkMode ? "0 2px 4px rgba(0, 0, 0, 0.6)" : "0 1px 2px rgba(0, 0, 0, 0.3)",
              fontWeight: 700,
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              px: 2,
              py: 1.3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.7,
              borderRadius: "10px",
              boxShadow: darkMode
                ? "0 6px 20px rgba(33, 150, 243, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                : "0 6px 20px rgba(33, 150, 243, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
              transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                pointerEvents: "none",
              },
              "&:hover:not(:disabled)": {
                transform: "translateY(-4px) scale(1.05)",
                boxShadow: darkMode
                  ? "0 12px 32px rgba(33, 150, 243, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                  : "0 12px 32px rgba(33, 150, 243, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 18 }} />
            {uploading ? `${progress}%` : "Upload"}
          </MDButton>
        </Stack>

        {/* Audio Player */}
        {blobUrl && (
          <MDBox
            sx={{
              py: 2,
              px: 2,
              background: darkMode
                ? "rgba(66, 165, 245, 0.08)"
                : "rgba(33, 150, 243, 0.05)",
              borderRadius: "12px",
              border: `1.5px solid ${
                darkMode
                  ? "rgba(66, 165, 245, 0.2)"
                  : "rgba(33, 150, 243, 0.15)"
              }`,
            }}
          >
            <MDTypography
              variant="body2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: darkMode ? "#90caf9" : "#1976d2",
              }}
            >
  
            </MDTypography>
            <audio
              src={blobUrl}
              controls
              style={{
                width: "100%",
                borderRadius: "8px",
                backgroundColor: darkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.05)",
              }}
            />
          </MDBox>
        )}

        {/* Upload Progress */}
        {uploading && (
          <MDBox>
            <MDBox sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <MDTypography variant="body2" sx={{ fontWeight: 600 }}>
                Uploading...
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {progress}%
              </MDTypography>
            </MDBox>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: "6px",
                borderRadius: "10px",
                background: darkMode
                  ? "rgba(66, 165, 245, 0.1)"
                  : "rgba(33, 150, 243, 0.1)",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #2196f3 0%, #42a5f5 100%)",
                  borderRadius: "10px",
                  boxShadow: "0 0 20px rgba(33, 150, 243, 0.5)",
                },
              }}
            />
          </MDBox>
        )}

        {/* Error Message */}
        {error && (
          <MDBox
            sx={{
              py: 2,
              px: 2,
              background: "rgba(244, 67, 54, 0.1)",
              borderRadius: "12px",
              border: "1.5px solid rgba(244, 67, 54, 0.3)",
            }}
          >
            <MDTypography
              variant="body2"
              sx={{
                color: "#ff4444",
                fontWeight: 600,
              }}
            >
              ❌ {error}
            </MDTypography>
          </MDBox>
        )}
      </Stack>
    </Card>
  );
};

AudioRecorder.propTypes = {
  uploadUrl: PropTypes.string.isRequired,
  method: PropTypes.oneOf(["POST", "PUT"]),
  headers: PropTypes.object,
  fieldName: PropTypes.string,
  filename: PropTypes.string,
  bitRate: PropTypes.number,
  uploadAs: PropTypes.oneOf(["form", "binary"]),
  autoUpload: PropTypes.bool,
  extraFormData: PropTypes.object,
  onUploadStart: PropTypes.func,
  onUploadProgress: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
};

export default AudioRecorder;
